import React, { useState, useRef, useId } from 'react';
import { Platform, TouchableWithoutFeedback, useWindowDimensions } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { Portal } from '@rn-primitives/portal';
import { colors, typography } from '@/lib/theme';
import { Box, Text, PressableBox } from './primitives';

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  width?: number;
}

export function Tooltip({ content, children, width = 200 }: TooltipProps) {
  const [interactionState, setInteractionState] = useState<'closed' | 'hover' | 'pressed'>('closed');
  const [coords, setCoords] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const triggerRef = useRef<any>(null);
  const { height: windowHeight, width: windowWidth } = useWindowDimensions();
  const portalName = useId();

  const measureAndSetCoords = (e?: any) => {
    if (Platform.OS === 'web') {
      // 1. Try event target (most reliable on Web)
      if (e?.currentTarget && typeof e.currentTarget.getBoundingClientRect === 'function') {
        const rect = e.currentTarget.getBoundingClientRect();
        setCoords({
          x: rect.left,
          y: rect.top,
          width: rect.width,
          height: rect.height,
        });
        return;
      }

      // 2. Fallback to ref measurement
      const node = triggerRef.current as any;
      if (!node) return;

      if (typeof node.getBoundingClientRect === 'function') {
        const rect = node.getBoundingClientRect();
        setCoords({
          x: rect.left,
          y: rect.top,
          width: rect.width,
          height: rect.height,
        });
      } else if (typeof node.measureInWindow === 'function') {
        node.measureInWindow((x: number, y: number, w: number, h: number) => {
          setCoords({ x, y, width: w, height: h });
        });
      }
    } else {
      // Native measurement
      triggerRef.current?.measureInWindow((x: number, y: number, w: number, h: number) => {
        setCoords({ x, y, width: w, height: h });
      });
    }
  };

  const handlePress = (e?: any) => {
    if (Platform.OS === 'web') {
      if (interactionState === 'pressed') {
        setInteractionState('closed');
      } else {
        measureAndSetCoords(e);
        setInteractionState('pressed');
      }
    } else {
      measureAndSetCoords();
      setInteractionState('pressed');
    }
  };

  const handleHoverIn = (e?: any) => {
    if (Platform.OS === 'web') {
      if (interactionState === 'closed') {
        measureAndSetCoords(e);
        setInteractionState('hover');
      }
    }
  };

  const handleHoverOut = () => {
    if (Platform.OS === 'web') {
      setInteractionState((prev) => (prev === 'hover' ? 'closed' : prev));
    }
  };

  const closeTooltip = () => setInteractionState('closed');

  // Calculations for Tooltip Positioning
  const centerX = coords.x + coords.width / 2;
  const halfWidth = width / 2;
  
  // Constrain Horizontal
  let horizontalShift = 0;
  if (centerX - halfWidth < 8) { // Left edge check
    horizontalShift = 8 - (centerX - halfWidth);
  } else if (centerX + halfWidth > windowWidth - 8) { // Right edge check
    horizontalShift = (windowWidth - 8) - (centerX + halfWidth);
  }

  // Constrain Vertical
  const showBelow = coords.y < 150;

  // Render the Tooltip content via Portal
  // We use TouchableWithoutFeedback on Native to creating a backdrop to close it
  const renderPortalContent = () => {
    if (interactionState === 'closed') return null;

    const tooltipStyle = {
      position: Platform.OS === 'web' ? 'fixed' : 'absolute',
      ...(showBelow ? { top: coords.y + coords.height + 8 } : { bottom: windowHeight - coords.y + 8 }),
      left: centerX,
      width,
      transform: [{ translateX: -halfWidth + horizontalShift }],
      padding: 8,
      borderColor: colors.ink.press,
      borderRadius: 0,
      borderWidth: 2,
      zIndex: 50,
      backgroundColor: colors.ink.press,
      elevation: 2,
      shadowColor: colors.ink.press,
      shadowOffset: { width: 4, height: 4 },
      shadowOpacity: 1,
      shadowRadius: 0,
    } as any;
    const arrowStyle = {
      position: 'absolute',
      ...(showBelow ? { bottom: '100%', marginBottom: 0 } : { top: '100%', marginTop: 0 }),
      left: '50%',
      width: 8,
      height: 8,
      backgroundColor: colors.ink.press,
      transform: [{ translateX: -4 - horizontalShift }, { rotate: '45deg' }],
      ...(showBelow ? { bottom: '100%', marginBottom: -4 } : { top: '100%', marginTop: -4 }),
    } as any;
    const backdropStyle = {
      position: 'fixed',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      zIndex: 49,
      cursor: 'default',
    } as any;
    const nativePortalStyle = {
      bottom: 0,
      left: 0,
      position: 'absolute',
      right: 0,
      top: 0,
      zIndex: 50,
    } as any;

    const tooltipBody = (
      <Box style={tooltipStyle}>
        <Box style={arrowStyle} />
        {typeof content === 'string' ? (
          <Text style={styles.tooltipText}>{content}</Text>
        ) : (
          content
        )}
      </Box>
    );

    if (Platform.OS === 'web') {
      return (
        <Portal name={portalName}>
          {interactionState === 'pressed' && (
            <PressableBox
              onPress={closeTooltip}
              style={backdropStyle}
            />
          )}
          {tooltipBody}
        </Portal>
      );
    }

    // On Native, wrap in a full-screen touchable to handle "click outside to close"
    return (
      <Portal name={portalName}>
        <TouchableWithoutFeedback onPress={closeTooltip}>
          <Box style={nativePortalStyle}>
             {tooltipBody}
          </Box>
        </TouchableWithoutFeedback>
      </Portal>
    );
  };

  return (
    <Box position="relative" zIndex={50}>
      <PressableBox
        ref={triggerRef}
        onPress={handlePress}
        // @ts-ignore
        onHoverIn={handleHoverIn}
        // @ts-ignore
        onHoverOut={handleHoverOut}
        style={Platform.OS === 'web' ? styles.webTrigger : undefined}
      >
        {children}
      </PressableBox>

      {renderPortalContent()}
    </Box>
  );
}

const styles = StyleSheet.create(() => ({
  tooltipText: {
    color: colors.text.inverse,
    fontFamily: typography.fontFamily.mono,
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    lineHeight: 16,
    textAlign: 'center',
    textTransform: 'uppercase',
  },
  webTrigger: {
    position: 'relative',
    zIndex: 45,
  },
}));
